import React, { useEffect, useState } from "react";
import { Form, Input, Tooltip, Select } from "antd";
import Button from "../../components/Button/Button";
import { useDispatch } from "react-redux";
import {
  createOrganizationForm,
  updateOrganizationForm,
} from "../../actions/admin";
import CountryStateCityPicker from "../../components/Comman/CountryStateCity/CountryStateCityPicker";
import CustomPhoneNumber from "../../components/Comman/CustomPhoneNumber/CustomPhoneNumber";
import ImageUpload from "../../components/Comman/ImageUpload";
import OrganizationListing from "../../components/Comman/DropdownsListing/OrganizationListing";
import { RcFile } from "antd/es/upload";

const { Option } = Select;

interface SideDrawerProps {
  organizationData?: any; // Optional organization data
  open?: boolean;
  onClose?: any;
}

const CreateOrganization: React.FC<SideDrawerProps> = ({
  organizationData,
  open,
  onClose,
}) => {
  const { TextArea } = Input;
  const [form] = Form.useForm();
  const dispatch: any = useDispatch();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [website, setWebsite] = useState("");
  const [profilePhoto, setProfilePhoto] = useState<RcFile | null>(null);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      // setLogoUrl(null);
    }
  }, [open]);

  const validateWebsite = (_: any, value: string) => {
    if (value) {
      const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/\S*)?$/;
      if (!urlPattern.test(value)) {
        return Promise.reject(new Error("Please enter a valid URL"));
      }
    }
    return Promise.resolve();
  };

  // Ensure website has "https://" prefix if missing
  const getValidURL = (url: string) => {
    return /^(https?:\/\/)/.test(url) ? url : `https://${url}`;
  };

  const onFinish = async (values: any) => {
    const formData = new FormData();
    values.logo = values.logo?.file || null;
    if (organizationData && values.logo === null) {
      delete values.logo;
    }
    if (values?.divisionOf === undefined) {
      delete values?.divisionOf;
    }
    if (profilePhoto) {
      values.logo = profilePhoto || null;
    }
    // Remove profilePhoto if it's null and userData exists
    if (values.logo === null) {
      delete values.logo;
    }
    Object.keys(values).forEach((key) => {
      const value = values[key];
      if (![null, undefined, "", "undefined"].includes(value)) {
        formData.append(key, value);
      }
    });

    if (organizationData) {
      dispatch(
        updateOrganizationForm(
          { data: formData, id: organizationData?.id }, // Ensure correct param names
          (resp: any) => {
            if (resp?.status === 200 || resp?.status === 201) {
              form.resetFields(); // Clear form on successful submission
              onClose();
            }
          }
        )
      );
    } else {
      dispatch(
        createOrganizationForm(formData, (resp: any) => {
          if (resp?.status === 200 || resp?.status === 201) {
            form.resetFields(); // Clear form on successful submission
            onClose();
          }
        })
      );
    }
  };

  useEffect(() => {
    if (organizationData || (open && organizationData)) {
      form.setFieldsValue({
        name: organizationData?.name || "",
        shortCode: organizationData?.shortCode || "",
        divisionOf: organizationData?.divisionOf || null,
        Organization_IsReporting:
          organizationData?.Organization_IsReporting ?? null,
        phone: organizationData?.phone || "",
        addr1: organizationData?.addr1 || "",
        addr2: organizationData?.addr2 || "",
        country: organizationData?.country || "",
        state: organizationData?.state || "",
        city: organizationData?.city || "",
        zip: organizationData?.zip || "",
        primare_contact_name: organizationData?.primare_contact_name || "",
        primare_contact_title: organizationData?.primare_contact_title || "",
        primare_contact_phone: organizationData?.primare_contact_phone || "",
        primare_contact_email: organizationData?.primare_contact_email || "",
        website: organizationData?.website || "",
        statement: organizationData?.statement || "",
        logo: organizationData?.logo || "",
      });
      setLogoUrl(organizationData?.logo || null);
    } else {
      form.resetFields();
      setLogoUrl(null);
    }
  }, [organizationData, open, form]); // Ensure effect runs when organizationData changes

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <div className="mb-[30px]">
        <h4 className="mb-2">Organization Details</h4>

        <div className="mb-[10px]">
          <Tooltip title="Organization name">
            <Form.Item
              name="name"
              rules={[
                { required: true, message: "Please enter organization name" },
              ]}
            >
              <Input
                type="text"
                placeholder="Organization Name"
                className="input-main-col focus:bg-transparent rounded-[5px] mb-[10px] p-[12px] 2xl:p-[15px]  border-1 border-transparent focus:border-white w-full bg-[#013351] placeholder-[#005A90] color-[#fff]"
              />
            </Form.Item>
          </Tooltip>
        </div>
        <div className="mb-[10px]">
          <Tooltip title="Organization Abbreviation">
            <Form.Item
              name="shortCode"
              rules={[
                {
                  required: true,
                  message: "Please enter organization abbreviation",
                },
                {
                  pattern: /^[A-Z]+$/,
                  message: "Only uppercase letters (A–Z) are allowed",
                },
                {
                  min: 3,
                  message: "Abbreviation must be at least 3 characters",
                },
              ]}
            >
              <Input
                type="text"
                placeholder="Organization Abbreviation"
                onChange={(e) => {
                  const upper = e.target.value.toUpperCase();
                  form.setFieldsValue({ shortCode: upper });
                }}
                maxLength={4}
                className="input-main-col focus:bg-transparent rounded-[5px] mb-[10px] p-[12px] 2xl:p-[15px]  border-1 border-transparent focus:border-white w-full bg-[#013351] placeholder-[#005A90] color-[#fff]"
              />
            </Form.Item>
          </Tooltip>
        </div>
        <OrganizationListing
          dataToSet={organizationData}
          pageName={"createOrganization"}
          keyName={"divisionOf"}
          idType={"organization"}
        />

        <div className="mb-[10px]">
          <Form.Item
            name="Organization_IsReporting"
            rules={[{ required: true, message: "Please select is reporting" }]}
          >
            <Select
              placeholder="Is Reporting"
              allowClear
              style={{ width: "100%" }}
              className="input-main-col focus:bg-transparent rounded-[5px] mb-[10px] p-[10px] border-1 border-transparent focus:border-white w-full bg-[#013351] placeholder-[#005A90] color-[#fff] h-auto"
            >
              <Option value={true}>Yes</Option>
              <Option value={false}>No</Option>
            </Select>
          </Form.Item>
        </div>

        <div className="mb-[20px]">
          <CustomPhoneNumber isRequired={true} />
        </div>

        <div className="mb-[10px]">
          <Tooltip title="Address">
            <Form.Item
              name="addr1"
              rules={[{ required: true, message: "Please enter address" }]}
            >
              <TextArea
                showCount
                maxLength={100}
                // onChange={onChange}
                placeholder="Address 1"
                className="input-main-col focus:bg-transparent rounded-[5px] mb-[10px] p-[12px] 2xl:p-[15px]  border-1 border-transparent focus:border-white w-full bg-[#013351] placeholder-[#005A90] color-[#fff]"
              />
            </Form.Item>
          </Tooltip>
        </div>

        <div className="mb-[10px]">
          <Tooltip title="Address">
            <Form.Item
              name="addr2"
              // rules={[{ required: true, message: "Please enter address" }]}
            >
              <TextArea
                showCount
                maxLength={100}
                // onChange={onChange}
                placeholder="Address 2"
                className="input-main-col focus:bg-transparent rounded-[5px] mb-[10px] p-[12px] 2xl:p-[15px]  border-1 border-transparent focus:border-white w-full bg-[#013351] placeholder-[#005A90] color-[#fff]"
              />
            </Form.Item>
          </Tooltip>
        </div>

        <div className="mb-[20px]">
          <CountryStateCityPicker
            initialCountry={organizationData?.country}
            initialState={organizationData?.state}
            initialCity={organizationData?.city}
            initialPostalCode={organizationData?.zip}
            form={form}
          />
        </div>
      </div>

      <div className="mb-[30px]">
        <h4 className="mb-2">Primary Details</h4>

        <div className="mb-[10px]">
          <Tooltip title="Contact name">
            <Form.Item
              name="primare_contact_name"
              rules={[{ required: true, message: "Please enter Org Leader" }]}
            >
              <Input
                type="text"
                placeholder="Contact Name"
                className="input-main-col focus:bg-transparent rounded-[5px] mb-[10px] p-[12px] 2xl:p-[15px]  border-1 border-transparent focus:border-white w-full bg-[#013351] placeholder-[#005A90] color-[#fff]"
              />
            </Form.Item>
          </Tooltip>
        </div>

        <div className="mb-[10px]">
          <Tooltip title="Contact title">
            <Form.Item
              name="primare_contact_title"
              // rules={[{ required: true, message: "Please select a title" }]}
            >
              <Input
                type="text"
                placeholder="Title"
                className="input-main-col focus:bg-transparent rounded-[5px] mb-[10px] p-[12px] 2xl:p-[15px]  border-1 border-transparent focus:border-white w-full bg-[#013351] placeholder-[#005A90] color-[#fff]"
              />
            </Form.Item>
          </Tooltip>
        </div>

        <div className="mb-[20px]">
          <CustomPhoneNumber name="primare_contact_phone" isRequired={true} />
        </div>
        <div className="mb-[10px]">
          <Tooltip title="Contact email">
            <Form.Item
              name="primare_contact_email"
              rules={[
                { required: true, message: "Please enter email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input
                type="email"
                placeholder="Email"
                className="input-main-col focus:bg-transparent rounded-[5px] mb-[10px] p-[12px] 2xl:p-[15px]  border-1 border-transparent focus:border-white w-full bg-[#013351] placeholder-[#005A90] color-[#fff]"
              />
            </Form.Item>
          </Tooltip>
        </div>

        <div className="mb-[30px]">
          <h4 className="mb-2">Website Details</h4>
          <div className="mb-[10px]">
            {/* <Tooltip title="website"> */}
            <Tooltip
              title={
                website &&
                /^(https?:\/\/)?([\w-]+(\.[\w-]+)+\/?)\S*$/.test(website) ? (
                  <a
                    href={getValidURL(website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#3DA57F",
                      textDecoration: "underline",
                      fontStyle: "italic",
                    }}
                    onClick={(e) => e.stopPropagation()} // Prevent tooltip from closing
                  >
                    Visit: {website}
                  </a>
                ) : (
                  "Enter a valid website"
                )
              }
            >
              <Form.Item
                name="website"
                rules={[{ validator: validateWebsite }]}
              >
                <Input
                  type="text"
                  // showCount
                  // maxLength={100}
                  // onChange={onChange}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="Website Address"
                  className="input-main-col focus:bg-transparent rounded-[5px] mb-[10px] p-[12px] 2xl:p-[15px]  border-1 border-transparent focus:border-white w-full bg-[#013351] placeholder-[#005A90] color-[#fff]"
                />
              </Form.Item>
            </Tooltip>
          </div>

          {/* <Form.Item name="logo" className="upload-input-col">
            <Upload {...uploadProps}>
              <Button
                type="button"
                buttonText="Upload"
                padding="py-[8px] px-[18px]"
                text="text-[12px] 2xl:text-sm"
              ></Button>
            </Upload>
          </Form.Item>
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Logo"
              className="mt-2 w-[100px] h-[100px] object-cover"
            />
          )} */}
          <div className="mb-[20px]">
            <ImageUpload
              logoUrl={logoUrl}
              imageName={"logo"}
              placeholder="Upload Business Logo"
              open={open}
              onFileSelect={(file) => setProfilePhoto(file)}
            />
          </div>

          <div className="mb-[10px]">
            <Tooltip title="Mission statement">
              <Form.Item
                name="statement"
                // rules={[
                //   { required: true, message: "Please enter mission statement" },
                // ]}
              >
                <TextArea
                  showCount
                  maxLength={500}
                  // onChange={onChange}
                  placeholder="Mission Statement"
                  className="input-main-col focus:bg-transparent rounded-[5px] mb-[10px] p-[12px] 2xl:p-[15px]  border-1 border-transparent focus:border-white w-full bg-[#013351] placeholder-[#005A90] color-[#fff]"
                />
              </Form.Item>
            </Tooltip>
          </div>
        </div>
        <div className="mb-[20px]">
          <Button
            type="submit"
            buttonText={organizationData ? "Update" : "submit"}
            padding="p-[18px]"
            text="text-[12px] 2xl:text-sm"
            width={"100%"}
          />
        </div>
      </div>
    </Form>
  );
};

export default CreateOrganization;
